import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, POP_UP_STYLE_CLASS, QUESTION_CODES, SECTION_CODES } from '../../constants/app-data.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { Mapper, MapperBuilder, TRANSFORM } from '../../helpers/mapper';
import { JobGridFilterRequest } from '../../interfaces/job-grid-filter-request.interface';
import { JobRunLogErrorListResponse } from '../../interfaces/job-run-log-error-list-response.interface';
import { JobRunLogInvoiceListResponse } from '../../interfaces/job-run-log-invoice-list-response.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';
import { JobRunInfoLogListResponse } from 'src/app/interfaces/job-run-info-log-list-response.interface';

@Component({
  selector: 'app-job-run-log-list',
  templateUrl: './job-run-log-list.component.html',
  styleUrl: './job-run-log-list.component.scss'
})
export class JobRunLogListComponent extends BaseCardSetListComponent implements OnInit {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';

  constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.jobRunLogList,
      () => API_ENDPOINTS.dashboard.getJobRunLog,
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.jobRunLogListForm.jobTypeFilter)
        .MapFrom(FILTER_QUESTION_CODES.jobRunLogListForm.jobOperationTypeFilter)
        .MapFrom(FILTER_QUESTION_CODES.jobRunLogListForm.jobStatusFilter)
        .MapFrom(FILTER_QUESTION_CODES.jobRunLogListForm.jobRunDateAfterFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.jobRunLogListForm.jobRunDateBeforeFilter, TRANSFORM.STRING_TO_DATE)
        .BuildMapper();
  }

  ngOnInit(): void {
  }

  formLoad(event: any): void {
    const filterRequest = this.getJobFilterRequest();

    this.loadGridDataOnFormLoad(filterRequest);
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.jobRunLogListForm.jobsGridFilter:
        {
          const filterRequest = this.getJobFilterRequest();
          this.loadGridData(filterRequest);
          break;
        }
    }
  }

  actionButton_jobsGrid_datagrid_killJob_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.jobRunLog.confirmKillJob;
    this.dialogService
      .showConfirm(this.activeDialog)
      .subscribe((response) => {
        if (response === DialogResult.ACCEPTED) {
          this.spinnerService.showSpinner();
          const endpoint = `${API_ENDPOINTS.job.kill}/${event.event.data.id}`;
          this.appDataService
            .post(endpoint, null)
            .pipe(
              take(1),
              this.errorService.errorHandler(),
              finalize(() => this.spinnerService.hideSpinner())
            )
            .subscribe((data) => {
              this.reloadPage();
            });
        }
      });
  }

  actionButton_jobsGrid_datagrid_viewInvoices_Clicked(event: any) {
    this.showInvoicesAsync(event.event.data.id);
  }

  actionButton_jobsGrid_datagrid_viewErrors_Clicked(event: any) {
    this.showErrorsAsync(event.event.data.id);
  }

  actionButton_jobsGrid_datagrid_viewInformation_Clicked(event: any) {
    this.showInformationAsync(event.event.data.id);
  }

  private getJobFilterRequest(): JobGridFilterRequest {
    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.jobRunLogListForm.jobs,
      PAGE_NUMBERS.jobRunLogListForm.jobs,
      SECTION_CODES.jobRunLogListForm.jobsGridSection,
      QUESTION_CODES.jobRunLogListForm.jobsGridFilter
    );

    let filterRequest: JobGridFilterRequest = {};
    if (filterModel != null) {
      filterRequest = this.filterQuestionToFilterModelMapper.Map(filterModel);
    }

    return filterRequest;
  }

  private async showInvoicesAsync(jobId: string) {
    const response: JobRunLogInvoiceListResponse = await this.getInvoicesAsync(jobId);

    if (response != null) {
      const messagePlaceholders = {
        jobReferenceNumber: response.jobReferenceNumber,
      };

      const popupModel = {
        'invoiceGrid-totalRecords': response.gridData.length,
        'invoiceGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.jobRunLog.invoices, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getInvoicesAsync(jobId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.job.getInvoices}/${jobId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }

  private async showErrorsAsync(jobId: string) {
    const response: JobRunLogErrorListResponse = await this.getErrorsAsync(jobId);

    if (response != null) {
      const messagePlaceholders = {
        jobReferenceNumber: response.jobReferenceNumber,
      };

      const popupModel = {
        'errorsGrid-totalRecords': response.gridData.length,
        'errorsGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.jobRunLog.errors, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getErrorsAsync(jobId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.job.getErrors}/${jobId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }

  private async showInformationAsync(jobId: string) {
    const response: JobRunInfoLogListResponse = await this.getInfoAsync(jobId);

    if (response != null) {
      const messagePlaceholders = {
        jobReferenceNumber: response.jobReferenceNumber,
        effectiveDate: response.effectiveDate,
      };

      const popupModel = {
        'informationGrid-totalRecords': response.gridData.length,
        'informationGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.jobRunLog.information, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getInfoAsync(jobId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.job.getInformation}/${jobId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }
}
